import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { Link } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import { tierForElo } from "@/skate/lib/rank";
import { MapPin, Search } from "lucide-react";

const SCENE_IMG = "https://images.pexels.com/photos/27733613/pexels-photo-27733613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Scene = () => {
  const { user } = useAuth();
  const [skaters, setSkaters] = useState([]);
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          api.get(`/users?city=${encodeURIComponent(user?.city || "")}&limit=30`),
          api.get(`/cities`),
        ]);
        setSkaters(u.data.filter((x) => x.user_id !== user.user_id));
        setCities(c.data);
      } catch {}
    })();
  }, [user]);

  const filtered = skaters.filter((s) =>
    !q || s.username?.toLowerCase().includes(q.toLowerCase()) || s.name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="scene-page">
      {/* Hero */}
      <div className="relative border border-zinc-800 overflow-hidden mb-6">
        <div className="absolute inset-0" style={{
          backgroundImage: `url(${SCENE_IMG})`, backgroundSize: "cover", backgroundPosition: "center",
          filter: "grayscale(60%) contrast(1.1)",
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 p-6">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#FACC15]">▌ LOCAL SCENE</div>
          <h1 className="font-display text-5xl leading-none mt-2">{user?.city?.toUpperCase() || "YOUR CITY"}<br/><span className="text-[#FACC15]">.</span></h1>
          <p className="text-zinc-300 text-sm mt-3 max-w-md">Skaters in your area. Tap any to challenge them.</p>
        </div>
      </div>

      {/* Cities */}
      {cities.length > 0 && (
        <div className="mb-6">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">TOP CITIES</div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            {cities.map((c) => (
              <div key={c.city} className="shrink-0 border border-zinc-800 px-3 py-2 flex items-center gap-2" data-testid={`city-chip-${c.city}`}>
                <MapPin className="w-3 h-3 text-zinc-500" />
                <span className="font-mono text-xs">{c.city}</span>
                <span className="font-mono text-[10px] text-zinc-500">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="border border-zinc-800 p-3 flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search skaters…"
          data-testid="scene-search-input"
          className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-zinc-600"
        />
      </div>

      {/* Skater grid */}
      <div className="grid grid-cols-1 gap-3">
        {filtered.length === 0 && (
          <div className="border border-dashed border-zinc-800 p-10 text-center font-mono text-xs text-zinc-500 tracking-widest">
            NO LOCAL SKATERS YET
          </div>
        )}
        {filtered.map((s) => {
          const tier = tierForElo(s.elo);
          return (
            <Link
              to={`/skate/skater/${s.user_id}`}
              key={s.user_id}
              data-testid={`scene-skater-${s.user_id}`}
              className="border border-zinc-800 hover:border-[#FACC15] transition-colors p-4 flex items-center gap-3"
            >
              <SkaterAvatar user={s} size={48} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg truncate">@{s.username}</span>
                  {s.verified && <span className="font-mono text-[9px] tracking-widest text-[#FACC15] border border-[#FACC15] px-1.5">✓</span>}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 tracking-wider">
                  <span style={{color: tier.color}}>{tier.name}</span> · {s.elo} ELO · {s.wins}W
                </div>
              </div>
              <span className="font-mono text-[10px] text-zinc-500 tracking-widest">CHALLENGE →</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Scene;
